'use client'

import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import type { NextPage } from 'next'
import { useEffect, useMemo } from 'react'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { cn } from '@cocoonspace/ui/lib/utils'
import { AnimatePresence } from 'motion/react'
import dynamic from 'next/dynamic'

import {
	ContentWrapper,
	FiltersWrapper,
	ListWrapper,
	MapWrapper,
	PageWrapper,
} from '~/components/layouts/search-layout'
import {
	SearchProvider,
	useSearchSpaces,
} from '~/domains/search/hooks/use-search-spaces.hook'
import { NoSpacesFound } from '../no-spaces-found'
import { SpaceCardSkeleton } from '../space-card'
import { ResetSearchButton } from './reset-search-button'
import { SpacesGroup } from './spaces-group'

const SpaceSearchFilters = dynamic(
	() =>
		import('~/domains/search/components/filters/space-search-filters').then(
			({ SpaceSearchFilters }) => SpaceSearchFilters,
		),
	{ ssr: false },
)

const SearchMap = dynamic(
	() => import('../search-map').then((mod) => mod.SearchMap),
	{ ssr: false },
)

const SpacesListSkeleton = () => (
	<div
		data-testid='spaces-layout-group'
		className={cn(
			'grid grid-cols-1 gap-4 px-4 pt-[2px]',
			'group-data-[map-visible="true"]:lg:grid-cols-2',
			'group-data-[map-visible="false"]:md:grid-cols-2 group-data-[map-visible="false"]:lg:grid-cols-3 group-data-[map-visible="false"]:xl:grid-cols-4',
		)}
	>
		{Array.from({ length: 6 }).map((_, i) => (
			<div
				key={i}
				className='mb-4'
			>
				<SpaceCardSkeleton />
			</div>
		))}
	</div>
)

export const SearchPageContent: NextPage = () => {
	const isMobile = useIsMobile()

	const {
		isMapVisible,
		filters,
		activeAlternative,
		spacesListQuery,
		hasAlternatives,
		hasNoResults,
		setIsMapVisible,
	} = useSearchSpaces()

	const timeSlot = useMemo(
		() => ({
			day: filters.day,
			end: filters.end,
			start: filters.start,
		}),
		[filters.day, filters.end, filters.start],
	)

	const alternatives = useMemo(
		() =>
			spacesListQuery.data?.meta?.alternatives?.map((alternative) => ({
				...alternative,
				timeSlot: {
					day: [alternative.day],
					end: alternative.end,
					start: alternative.start,
				},
			})) || [],
		[spacesListQuery.data?.meta?.alternatives],
	)

	// hide the map if loaded from mobile
	useEffect(() => {
		if (isMobile) {
			setIsMapVisible(false)
		}
	}, [isMobile, setIsMapVisible])

	return (
		<PageWrapper
			className='group'
			data-map-visible={isMapVisible.toString()}
		>
			<FiltersWrapper>
				<SpaceSearchFilters />
			</FiltersWrapper>

			<ContentWrapper>
				<ListWrapper className='overflow-y-auto'>
					{spacesListQuery.isLoading && <SpacesListSkeleton />}

					{hasNoResults && (
						<NoSpacesFound
							className='px-4'
							hasAlternatives={hasAlternatives}
						/>
					)}

					{!hasNoResults && (
						<SpacesGroup
							items={spacesListQuery.data?.data || []}
							timeSlot={timeSlot}
						/>
					)}

					{alternatives.map((alternative) => (
						<SpacesGroup
							key={`alternative-${alternative.label}`}
							label={alternative.label}
							timeSlot={alternative.timeSlot}
							items={alternative.data || []}
							isAlternative
						/>
					))}
				</ListWrapper>

				<MapWrapper>
					<AnimatePresence>
						{activeAlternative && isMapVisible && <ResetSearchButton />}
					</AnimatePresence>

					<SearchMap />
				</MapWrapper>
			</ContentWrapper>
		</PageWrapper>
	)
}

export const SearchPage: NextPage<{
	zone: Zone
}> = ({ zone, ...props }) => (
	<SearchProvider zone={zone}>
		<SearchPageContent {...props} />
	</SearchProvider>
)
